import { css } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import type { ButtonSize } from '../button.type';

export const buttonBase = css`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  display: inline-flex;
  font-family: ${theme.font.family};
  font-weight: ${theme.font.weight.bold};
  justify-content: center;
  margin: 0;
  padding: 0;
  position: relative;
  text-transform: capitalize;
  text-wrap: nowrap;
  transition: background 150ms ease-out, color 150ms ease-out, border-color 150ms ease-out;
  box-sizing: border-box;
  &[aria-disabled='true'] {
    cursor: default;
  }
`;

const LARGE = css`
  font-size: ${theme.fontSize(16)};
  height: 40px;
  line-height: 1.375;
  padding: 8px 12px;
`;

const LARGE_ICON = css`
  font-size: ${theme.fontSize(16)};
  height: 40px;
  line-height: 1.375;
  padding: 8px;
`;

const MEDIUM = css`
  font-size: ${theme.fontSize(14)};
  height: 36px;
  line-height: 1.375;
  padding: 8px 12px;
`;

const MEDIUM_ICON = css`
  font-size: ${theme.fontSize(14)};
  height: 36px;
  line-height: 1.375;
  padding: 8px;
`;

const SMALL = css`
  font-size: ${theme.fontSize(14)};
  height: 32px;
  line-height: 1.375;
  padding: 8px 12px;
`;

const SMALL_ICON = css`
  font-size: ${theme.fontSize(14)};
  height: 32px;
  line-height: 1.375;
  padding: 8px;
`;

const TINY = css`
  font-size: ${theme.fontSize(12)};
  height: 20px;
  line-height: 1;
  padding: 4px;
`;

const TINY_ICON = css`
  font-size: ${theme.fontSize(12)};
  height: 20px;
  width: 20px;
  line-height: 1;
  padding: 0;
`;

const BUTTON_SIZE_CLASS = {
  large: {
    normal: LARGE,
    icon: LARGE_ICON,
  },
  medium: {
    normal: MEDIUM,
    icon: MEDIUM_ICON,
  },
  small: {
    normal: SMALL,
    icon: SMALL_ICON,
  },
  tiny: {
    normal: TINY,
    icon: TINY_ICON,
  },
} as const;

export const buttonContentBase = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CONTENT_LARGE = css`
  min-width: 24px;
`;

const CONTENT_DEFAULT = css`
  min-width: 16px;
`;

const CONTENT_TINY = css`
  min-width: 14px;
`;

const BUTTON_CONTENT_SIZE_CLASS = {
  large: CONTENT_LARGE,
  medium: CONTENT_DEFAULT,
  small: CONTENT_DEFAULT,
  tiny: CONTENT_TINY,
} as const;

export const getButtonContentClassName = (size: ButtonSize) => BUTTON_CONTENT_SIZE_CLASS[size];

export const getButtonSizeClassName = (size: ButtonSize, iconOnly: boolean) =>
  BUTTON_SIZE_CLASS[size][iconOnly ? 'icon' : 'normal'];

export const primaryFocus = css`
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px white, 0 0 0 4px ${theme.colors.primary50};
  }

  &:focus:not(:active)::before,
  &:focus-within:not(:active)::before {
    opacity: 1;
  }
`;

export const neutralFocus = css`
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px white, 0 0 0 4px ${theme.colors.neutral50};
  }

  &:focus:not(:active)::before,
  &:focus-within:not(:active)::before {
    opacity: 1;
  }
`;

export const criticalFocus = css`
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px white, 0 0 0 4px ${theme.colors.status.critical};
  }

  &:focus:not(:active)::before,
  &:focus-within:not(:active)::before {
    opacity: 1;
  }
`;

export const primaryFocusOutline = css`
  &:focus {
    box-shadow: 0 0 0 2px white, 0 0 0 4px ${theme.colors.primary50};
  }
`;

export const neutralFocusOutline = css`
  &:focus {
    box-shadow: 0 0 0 2px white, 0 0 0 4px ${theme.colors.neutral50};
  }
`;

export const criticalFocusOutline = css`
  &:focus {
    box-shadow: 0 0 0 2px white, 0 0 0 4px ${theme.colors.status.critical};
  }
`;

export type FocusVariant = 'primary' | 'neutral' | 'critical';

const FOCUS_CLASS = {
  primary: primaryFocus,
  neutral: neutralFocus,
  critical: criticalFocus,
} as const;

const FOCUS_OUTLINE_CLASS = {
  primary: primaryFocusOutline,
  neutral: neutralFocusOutline,
  critical: criticalFocusOutline,
} as const;

export const getFocusClassName = (variant: FocusVariant) => FOCUS_CLASS[variant];

export const getFocusOutlineClassName = (variant: FocusVariant) => FOCUS_OUTLINE_CLASS[variant];
